import {
  Box,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  useDisclosure,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tag,
  Badge,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  useColorModeValue,
  Icon,
  Progress,
  Tooltip,
  Link,
  Image,
  Wrap,
  WrapItem,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Kbd,
  Stack,
} from "@chakra-ui/react";
import {
  FiUser,
  FiFileText,
  FiClock,
  FiFilter,
  FiMessageCircle,
  FiCheckCircle,
  FiExternalLink,
  FiLayers,
  FiTarget,
} from "react-icons/fi";
import { useQuery, useQueryClient } from "react-query";
import { useMemo, useState } from "react";
import { getStaffSubmittedQuestionPapers, updateAdminReview } from "API/attemptReviewApi";
import { STATIC_PREFIX } from "utils/constants";

/* --------------------- Helper Functions --------------------- */

// Get backend base URL
const getBackendBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
  }
  return "";
};

// Build backend image URL from filename or path
const buildImageUrl = (img?: string | null): string => {
  if (!img || typeof img !== "string") return "";

  // If already a full URL, return as-is
  if (/^https?:\/\//i.test(img)) return img;

  const base = getBackendBaseUrl();

  // If it starts with STATIC_PREFIX, use directly
  if (img.startsWith(STATIC_PREFIX + "/")) {
    return `${base}${img}`;
  }

  // Otherwise, assume it's a filename and prepend STATIC_PREFIX
  return `${base}${STATIC_PREFIX}/${encodeURIComponent(img)}`;
};

// Normalize upload path
const normalizeUploadPath = (val?: string | null): string | null => {
  if (!val || typeof val !== "string") return null;
  return buildImageUrl(val) || null;
};

const toAbsUrl = (u?: string | null) => {
  if (!u || typeof u !== "string") return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = getBackendBaseUrl();
  return `${base}${u.startsWith("/") ? "" : "/"}${u}`;
};

// Check if question type is auto-checkable
const isCheckableType = (type?: string) => {
  if (!type) return false;
  const t = type.toLowerCase();
  return t === "mcqs" || t === "mcqm" || t === "tf";
};

// Get MCQM submitted answer array
const getMcqmSubmittedArray = (item: any): number[] => {
  const arr =
    item?.mcqmSubmittedAnswer != null
      ? item.mcqmSubmittedAnswer
      : item?.mcqmSubmittedAnswers != null
        ? item.mcqmSubmittedAnswers
        : [];
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => Number(v)).filter((n) => Number.isFinite(n));
};

// Compare arrays ignoring order
const compareArraysIgnoreOrder = (a: any[], b: any[]) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const A = [...a].map(Number).sort((x, y) => x - y);
  const B = [...b].map(Number).sort((x, y) => x - y);
  return A.every((v, i) => v === B[i]);
};

// Auto-check if answer is correct
const autoCheckCorrect = (item: any): boolean | undefined => {
  const q = item?.question;
  const type = q?.type?.toLowerCase();
  if (!type) return undefined;

  if (type === "mcqs") {
    return Number(item.mcqsSubmittedAnswer) === Number(q?.mcqSAnswer);
  }
  if (type === "mcqm") {
    const submitted = getMcqmSubmittedArray(item);
    const correct = (q?.mcqMAnswer || []).map((v: any) => Number(v));
    return compareArraysIgnoreOrder(submitted, correct);
  }
  if (type === "tf") {
    const val =
      typeof item.tfSubmittedAnswer === "string"
        ? item.tfSubmittedAnswer.toLowerCase() === "true"
        : Boolean(item.tfSubmittedAnswer);
    return val === Boolean(q?.tFAnswer);
  }
  return undefined;
};

// Format student's answer for display
const formatStudentAnswer = (item: any): string => {
  const q = item?.question;
  const type = q?.type?.toLowerCase();

  if (type === "mcqs") {
    const idx = item.mcqsSubmittedAnswer;
    return q?.mcqSOptions?.[Number(idx)] ?? (idx !== undefined ? String(idx) : "—");
  }
  if (type === "mcqm") {
    const arr = getMcqmSubmittedArray(item);
    if (!arr.length) return "—";
    return arr.map((i: number) => q?.mcqMOptions?.[Number(i)] ?? String(i)).join(", ");
  }
  if (type === "tf") {
    if (typeof item.tfSubmittedAnswer === "boolean") return item.tfSubmittedAnswer ? "True" : "False";
    const v = String(item.tfSubmittedAnswer || "").toLowerCase();
    return v === "true" ? "True" : v === "false" ? "False" : "—";
  }
  if (type === "short") return item.shortSubmittedAnswer ?? "—";
  if (type === "long" || type === "la") return item.longSubmittedAnswer ?? "—";
  if (type === "diagram") return item.diagramSubmittedAnswer ?? "—";
  return item.answer ?? "—";
};

// Format correct answer for display
const formatCorrectAnswer = (item: any): string => {
  const q = item?.question;
  const type = q?.type?.toLowerCase();

  if (type === "mcqs") {
    return q?.mcqSOptions?.[q?.mcqSAnswer] ?? (q?.mcqSAnswer !== undefined ? String(q.mcqSAnswer) : "—");
  }
  if (type === "mcqm") {
    const arr = q?.mcqMAnswer || [];
    if (!arr.length) return "—";
    return arr.map((i: number) => q?.mcqMOptions?.[Number(i)] ?? String(i)).join(", ");
  }
  if (type === "tf") {
    return typeof q?.tFAnswer === "boolean" ? (q.tFAnswer ? "True" : "False") : "—";
  }
  return "Manual Review";
};

// Get final correctness status
const getFinalCorrectness = (item: any): boolean | null => {
  if (!item.attempted) return false;
  const type = item?.question?.type?.toLowerCase();
  if (typeof item.isCorrect === "boolean") return item.isCorrect;
  if (isCheckableType(type)) return autoCheckCorrect(item) === true;
  return null;
};

// Get question ID
const getIdFromQuestion = (q: any) => q?._id || q?.id || q?.questionId;

// Build marks map from paper sections
const buildPossibleMarksMap = (paper: any): Map<string, number> => {
  const map = new Map<string, number>();
  const sections = paper?.sections || [];
  sections.forEach((sec: any) => {
    (sec.questions || []).forEach((it: any) => {
      if (it?.questionId) {
        map.set(String(it.questionId), Number(it.marks) || 0);
      }
    });
  });
  return map;
};

// Compute awarded marks for a question
const computeAwardedMarks = (item: any, possibleMap: Map<string, number>): number => {
  if (!item.attempted) return 0;
  const qid = String(getIdFromQuestion(item.question) || item.question);
  const possible = possibleMap.get(qid) || 0;
  const correctness = getFinalCorrectness(item);
  return correctness === true ? possible : 0;
};

// Compute comprehensive statistics
const computeComprehensiveStats = (questionResult: any[] = []) => {
  const all = questionResult || [];
  const attempted = all.filter((it) => it.attempted);
  const correct = all.filter((it) => it.attempted && getFinalCorrectness(it) === true);
  const wrong = all.filter((it) => it.attempted && getFinalCorrectness(it) === false);
  const notAttempted = all.filter((it) => !it.attempted);
  const pending = all.filter((it) => it.attempted && getFinalCorrectness(it) === null);

  const total = all.length;
  const scorePct = attempted.length ? Math.round((correct.length / attempted.length) * 100) : 0;

  return {
    total,
    attempted: attempted.length,
    correct: correct.length,
    wrong: wrong.length,
    notAttempted: notAttempted.length,
    pending: pending.length,
    scorePct,
  };
};

// Compute totals for an attempt
const computeTotalsForAttempt = (attempt: any) => {
  const paper = attempt?.questionPaper || {};
  const questionResult = attempt?.questionResult || [];
  const possibleMap = buildPossibleMarksMap(paper);

  const totalPossibleRaw = questionResult.reduce((acc: number, it: any) => {
    const qid = String(getIdFromQuestion(it.question) || it.question);
    return acc + (possibleMap.get(qid) || 0);
  }, 0);

  const cap = Number(paper?.totalMarks || attempt?.totalMarks || 0);
  const totalPossibleCapped = cap ? Math.min(totalPossibleRaw, cap) : totalPossibleRaw;

  const awardedRaw = questionResult.reduce((acc: number, it: any) => {
    return acc + computeAwardedMarks(it, possibleMap);
  }, 0);
  const awarded = Math.min(awardedRaw, totalPossibleCapped);

  const percent = totalPossibleCapped > 0 ? Math.round((awarded / totalPossibleCapped) * 100) : 0;

  return { possibleMap, totalPossibleCapped, awarded, percent };
};

// Get justification/solution image URL
const getJustificationUrl = (item: any): string | null => {
  if (!item) return null;
  const q = item.question || {};

  const candidates: Array<string | undefined> = [
    item.justificationImg,
    q.justificationImg,
    q.solutionImg,
    typeof q.justification === "string" ? q.justification : undefined,
  ];

  for (const c of candidates) {
    const url = normalizeUploadPath(c);
    if (url) return url;
  }
  return null;
};

/* --------------------- Image Components --------------------- */

// Render option image for MCQ
const renderOptionImage = (imageUrl: string, alt: string) => {
  const bg = useColorModeValue("gray.50", "gray.900");
  if (!imageUrl) return null;

  return (
    <Box
      mt={1}
      mb={1}
      borderWidth="1px"
      borderRadius="md"
      p={1}
      bg={bg}
      display="inline-block"
      maxW="120px"
      maxH="120px"
    >
      <Image
        src={imageUrl}
        alt={alt}
        maxH="120px"
        objectFit="contain"
        borderRadius="md"
        onError={(e) => {
          console.error("Failed to load option image:", imageUrl);
          e.currentTarget.style.display = 'none';
        }}
      />
    </Box>
  );
};

// Options block for MCQ questions
const OptionsBlock = ({ item }: { item: any }) => {
  const q = item?.question;
  const type = (q?.type || "").toLowerCase();
  if (type !== "mcqs" && type !== "mcqm") return null;

  const isMCQS = type === "mcqs";
  const options = isMCQS ? q?.mcqSOptions || [] : q?.mcqMOptions || [];
  const optionImages = isMCQS ? q?.mcqSOptionImages || [] : q?.mcqMOptionImages || [];

  const userAnswer = isMCQS
    ? Number(item?.mcqsSubmittedAnswer)
    : getMcqmSubmittedArray(item);

  return (
    <Box mt={2} mb={2}>
      <Stack direction="column" spacing={2}>
        {options.map((opt: string, idx: number) => {
          const imageFilename = optionImages[idx];
          const imageUrl = buildImageUrl(imageFilename);

          const isSelected = isMCQS
            ? userAnswer === idx
            : Array.isArray(userAnswer) && userAnswer.includes(idx);

          return (
            <HStack
              key={idx}
              align="start"
              spacing={3}
              bg={isSelected ? "teal.50" : undefined}
              p={2}
              borderRadius="md"
            >
              <Box
                minW="18px"
                fontWeight={isSelected ? "bold" : "normal"}
                color={isSelected ? "teal.600" : undefined}
              >
                <Box as="span">{String.fromCharCode(65 + idx)}.</Box>
              </Box>
              <Box flex="1">
                {opt && opt.trim() && <Text as="span" mb={1}>{opt}</Text>}
                {imageUrl && renderOptionImage(imageUrl, `Option ${String.fromCharCode(65 + idx)}`)}
              </Box>
            </HStack>
          );
        })}
      </Stack>
    </Box>
  );
};

// Question image component
const QuestionImage = ({ question }: { question: any }) => {
  const src = normalizeUploadPath(question?.questionImg);
  const frameBg = useColorModeValue("gray.50", "gray.900");
  const border = useColorModeValue("gray.200", "gray.700");

  if (!src) return null;

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
      w="100%"
      maxH="300px"
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      bg={frameBg}
      mt={3}
      borderColor={border}
    >
      <Image
        src={src}
        alt="Question"
        maxW="100%"
        maxH="300px"
        objectFit="contain"
        onError={(e) => {
          console.error("Failed to load question image:", src);
        }}
      />
    </Box>
  );
};

// Diagram image component
const DiagramImage = ({ question }: { question: any }) => {
  const src = normalizeUploadPath(question?.questionImg);
  const frameBg = useColorModeValue("gray.50", "gray.900");
  const border = useColorModeValue("gray.200", "gray.700");

  if (!src) {
    return (
      <Box mt={3} color={useColorModeValue("gray.600", "gray.300")} fontSize="xs">
        No diagram available
      </Box>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
      w="100%"
      maxH="300px"
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      bg={frameBg}
      mt={3}
      borderColor={border}
    >
      <Image
        src={src}
        alt="Diagram"
        maxW="100%"
        maxH="300px"
        objectFit="contain"
        onError={(e) => {
          console.error("Failed to load diagram:", src);
        }}
      />
    </Box>
  );
};

// Justification image component
const JustificationImage = ({ item }: { item: any }) => {
  const src = getJustificationUrl(item);
  if (!src) return null;

  const frameBg = useColorModeValue("gray.50", "gray.900");
  const border = useColorModeValue("gray.200", "gray.700");

  return (
    <Box mt={3}>
      <Text fontSize="xs" color={useColorModeValue("gray.600", "gray.300")} mb={2} fontWeight="medium">
        Justification
      </Text>
      <Box
        borderWidth="1px"
        borderRadius="md"
        overflow="hidden"
        w="100%"
        maxH="250px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={frameBg}
        borderColor={border}
      >
        <Image
          src={src}
          alt="Justification"
          maxW="100%"
          maxH="250px"
          objectFit="contain"
          onError={(e) => {
            console.error("Failed to load justification:", src);
          }}
        />
      </Box>
      <Box mt={2}>
        <Link href={src} isExternal color="blue.500" display="inline-flex" alignItems="center" fontSize="xs">
          View Full Size <Icon as={FiExternalLink} ml={1} boxSize="12px" />
        </Link>
      </Box>
    </Box>
  );
};

// Submitted uploads component
const SubmittedUploads = ({ uploads }: { uploads?: string[] }) => {
  if (!Array.isArray(uploads) || uploads.length === 0) return null;

  const frameBg = useColorModeValue("gray.50", "gray.900");
  const border = useColorModeValue("gray.200", "gray.700");

  const normalized = uploads.map((u) => toAbsUrl(u || "")).filter(Boolean) as string[];
  const imageLike = normalized.filter((u) => /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(u));
  const others = normalized.filter((u) => !imageLike.includes(u));

  return (
    <Box mt={3}>
      <Text fontSize="xs" color={useColorModeValue("gray.600", "gray.300")} mb={2} fontWeight="medium">
        Submitted Files
      </Text>

      {imageLike.length > 0 && (
        <VStack spacing={3} align="stretch">
          {imageLike.map((src, i) => (
            <Box key={`${src}-${i}`}>
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                bg={frameBg}
                borderColor={border}
                maxW="100%"
              >
                <Image
                  src={src}
                  alt="Submitted"
                  maxH="250px"
                  objectFit="contain"
                  w="100%"
                  bg={frameBg}
                  onError={(e) => {
                    console.error("Failed to load uploaded image:", src);
                  }}
                />
              </Box>
              <Box mt={1}>
                <Link href={src} isExternal color="blue.500" display="inline-flex" alignItems="center" fontSize="xs">
                  View Full Size <Icon as={FiExternalLink} ml={1} boxSize="12px" />
                </Link>
              </Box>
            </Box>
          ))}
        </VStack>
      )}

      {others.length > 0 && (
        <VStack align="start" spacing={1} mt={imageLike.length ? 3 : 0}>
          {others.map((href, i) => (
            <Link
              key={`${href}-${i}`}
              href={href}
              isExternal
              color="blue.500"
              display="inline-flex"
              alignItems="center"
              fontSize="xs"
            >
              {href.split("/").pop() || "file"} <Icon as={FiExternalLink} ml={1} boxSize="12px" />
            </Link>
          ))}
        </VStack>
      )}
    </Box>
  );
};

/* --------------------- Main Component --------------------- */
export default function AttemptReviewAdmin() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bg = useColorModeValue("white", "gray.900");
  const surface = useColorModeValue("gray.50", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const title = useColorModeValue("gray.900", "gray.100");
  const text = useColorModeValue("gray.700", "gray.300");
  const muted = useColorModeValue("gray.500", "gray.400");

  const { data, isLoading, isError, error } = useQuery("allSubmissions", getStaffSubmittedQuestionPapers, {
    onError: (err: any) =>
      toast({
        title: "Failed to load submissions",
        description: err?.message || "Unknown error",
        status: "error",
        isClosable: true,
      }),
  });

  const [selectedAttempt, setSelectedAttempt] = useState<any>(null);
  const [reviewData, setReviewData] = useState<any[]>([]);
  const [overallComment, setOverallComment] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "reviewed">("all");

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const arr = [...data];
    arr.sort((a: any, b: any) => {
      const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
      if (bt !== at) return bt - at;
      return String(b._id).localeCompare(String(a._id));
    });
    return arr;
  }, [data]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(sortedData)) return [];
    if (filter === "pending") return sortedData.filter((a: any) => a.isResultPending);
    if (filter === "reviewed") return sortedData.filter((a: any) => !a.isResultPending);
    return sortedData;
  }, [sortedData, filter]);

  const total = sortedData.length || 0;
  const pendingCount = sortedData.filter((a: any) => a.isResultPending)?.length || 0;
  const reviewedCount = total - pendingCount;

  const handleReviewClick = (attempt: any) => {
    const prechecked = (attempt.questionResult || []).map((item: any) => {
      const base = { ...item };
      if (Array.isArray(base.mcqmSubmittedAnswers) && !Array.isArray(base.mcqmSubmittedAnswer)) {
        base.mcqmSubmittedAnswer = base.mcqmSubmittedAnswers.map((v: any) => Number(v));
      }
      return base;
    });

    prechecked.sort((a: any, b: any) => {
      const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
      if (bt !== at) return bt - at;
      return String(b.question?._id || b._id || "").localeCompare(String(a.question?._id || a._id || ""));
    });

    setSelectedAttempt(attempt);
    setReviewData(prechecked);
    setOverallComment(attempt.adminComment || "");
    onOpen();
  };

  const updateReviewItem = (index: number, patch: Partial<any>) => {
    setReviewData((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const possibleMap = useMemo(() => {
    if (!selectedAttempt?.questionPaper) return new Map<string, number>();
    return buildPossibleMarksMap(selectedAttempt.questionPaper);
  }, [selectedAttempt]);

  const totalPossibleRaw = useMemo(() => {
    if (!reviewData.length) return 0;
    return reviewData.reduce((acc, it) => {
      const qid = String(getIdFromQuestion(it.question) || it.question);
      return acc + (possibleMap.get(qid) || 0);
    }, 0);
  }, [reviewData, possibleMap]);

  const totalPossibleCapped = useMemo(() => {
    const cap = Number(selectedAttempt?.questionPaper?.totalMarks || selectedAttempt?.totalMarks || 0);
    if (!cap) return totalPossibleRaw;
    return Math.min(totalPossibleRaw, cap);
  }, [totalPossibleRaw, selectedAttempt]);

  const totalAwardedComputed = useMemo(() => {
    if (!reviewData.length) return 0;
    const raw = reviewData.reduce((acc, it) => acc + computeAwardedMarks(it, possibleMap), 0);
    return Math.min(raw, totalPossibleCapped);
  }, [reviewData, possibleMap, totalPossibleCapped]);

  const overallPercent = useMemo(() => {
    const denom =
      totalPossibleCapped ||
      Number(selectedAttempt?.questionPaper?.totalMarks || selectedAttempt?.totalMarks || 0);
    return denom > 0 ? Math.round((totalAwardedComputed / denom) * 100) : 0;
  }, [totalAwardedComputed, totalPossibleCapped, selectedAttempt]);

  const handleSaveReview = async () => {
    try {
      const normalized = reviewData.map((it) => {
        const qid = String(getIdFromQuestion(it.question) || it.question);
        const possible = possibleMap.get(qid) || 0;

        let mcqm = it.mcqmSubmittedAnswer;
        if (!Array.isArray(mcqm) && Array.isArray(it.mcqmSubmittedAnswers)) {
          mcqm = it.mcqmSubmittedAnswers.map((v: any) => Number(v));
        }

        if (!it.attempted) {
          return { ...it, isCorrect: false, marks: 0, mcqmSubmittedAnswer: mcqm };
        }

        const finalCorrectness = getFinalCorrectness(it);
        const finalMarks = finalCorrectness === true ? possible : 0;

        return {
          ...it,
          mcqmSubmittedAnswer: mcqm,
          isCorrect: finalCorrectness,
          marks: finalMarks,
        };
      });

      await updateAdminReview(selectedAttempt._id, {
        questionResult: normalized,
        reviewedBy: "Admin",
        reviewDate: new Date().toISOString(),
        adminComment: overallComment,
        isResultPending: false,
        scoreObtained: totalAwardedComputed,
        totalMarks: totalPossibleCapped,
      });

      toast({ title: "Review saved", status: "success", duration: 2200 });
      onClose();
      queryClient.invalidateQueries("allSubmissions");
    } catch (err: any) {
      toast({
        title: "Error saving",
        description: err?.message || "Unknown error",
        status: "error",
        isClosable: true,
      });
    }
  };

  /* --------------------- Render --------------------- */
  if (isLoading)
    return (
      <Flex justify="center" align="center" h="60vh">
        <Spinner size="xl" />
      </Flex>
    );

  if (isError)
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {(error as Error)?.message || "Error"}
      </Alert>
    );

  if (!sortedData || sortedData.length === 0)
    return (
      <Box textAlign="center" py={12}>
        <Heading size="md" color={title}>
          No submissions found
        </Heading>
      </Box>
    );

  const headerCards = (
    <SimpleGrid columns={[1, 3]} spacing={4} mb={6}>
      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" p={4} boxShadow="sm">
        <Stat>
          <StatLabel color={muted} display="flex" alignItems="center" gap={2} mb={1} fontSize="xs">
            <Icon as={FiLayers} boxSize="14px" /> Total Submissions
          </StatLabel>
          <StatNumber color={title} fontSize="xl">{sortedData.length}</StatNumber>
          <StatHelpText color={text} mt={0} fontSize="xs">All attempts received</StatHelpText>
        </Stat>
      </Box>

      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" p={4} boxShadow="sm">
        <Stat>
          <StatLabel color={muted} display="flex" alignItems="center" gap={2} mb={1} fontSize="xs">
            <Icon as={FiClock} boxSize="14px" /> Pending Review
          </StatLabel>
          <StatNumber color="orange.500" fontSize="xl">{pendingCount}</StatNumber>
          <StatHelpText color={text} mt={0} fontSize="xs">Awaiting review</StatHelpText>
        </Stat>
      </Box>

      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" p={4} boxShadow="sm">
        <Stat>
          <StatLabel color={muted} display="flex" alignItems="center" gap={2} mb={1} fontSize="xs">
            <Icon as={FiCheckCircle} boxSize="14px" /> Completed
          </StatLabel>
          <StatNumber color="green.500" fontSize="xl">{reviewedCount}</StatNumber>
          <StatHelpText color={text} mt={0} fontSize="xs">Reviews completed</StatHelpText>
        </Stat>
      </Box>
    </SimpleGrid>
  );

  return (
    <Box maxW="1200px" mx="auto" p={[4, 6]}>
      {/* Header */}
      <Box bg={surface} p={6} borderRadius="xl" borderWidth="1px" borderColor={border} mb={6} boxShadow="sm">
        <Flex align="center" justify="space-between" wrap="wrap" gap={4} mb={4}>
          <Box>
            <Heading size="lg" color={title} mb={1}>
              Exam Reviews
            </Heading>
            <Text color={text} fontSize="sm">
              Review and evaluate student exam submissions
            </Text>
          </Box>

          <Select
            w="250px"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            bg={bg}
            borderColor={border}
            size="sm"
            fontSize="sm"
          >
            <option value="all">All Submissions ({total})</option>
            <option value="pending">Pending Review ({pendingCount})</option>
            <option value="reviewed">Reviewed ({reviewedCount})</option>
          </Select>
        </Flex>

        <Divider mb={4} />
        {headerCards}
      </Box>

      {/* Submissions List */}
      <Box bg={bg} borderRadius="xl" borderWidth="1px" borderColor={border} overflow="hidden" boxShadow="sm">
        <Accordion allowMultiple>
          {filteredData.map((att: any) => {
            const stats = computeComprehensiveStats(att.questionResult);
            const { awarded, totalPossibleCapped, percent } = computeTotalsForAttempt(att);
            const pctColor = percent >= 50 ? "green" : "red";
            const statusScheme = att.isResultPending ? "yellow" : "green";

            return (
              <AccordionItem key={att._id} borderColor={border}>
                <AccordionButton _expanded={{ bg: surface }} py={4} px={6}>
                  <Box flex="1" textAlign="left">
                    <VStack align="stretch" spacing={3}>
                      <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                        <HStack spacing={4}>
                          <HStack spacing={2}>
                            <Icon as={FiUser} color={muted} boxSize="16px" />
                            <Text fontWeight="bold" color={title} fontSize="md">
                              {att.student?.name}
                            </Text>
                          </HStack>

                          <Box h="16px" w="1px" bg={border} />

                          <Text color={text} fontWeight="medium" maxW="300px" noOfLines={1} fontSize="sm">
                            {att.questionPaper?.title}
                          </Text>
                        </HStack>

                        <Badge
                          variant="solid"
                          colorScheme={statusScheme}
                          px={2}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                        >
                          {att.isResultPending ? "Pending Review" : "Reviewed"}
                        </Badge>
                      </Flex>

                      <SimpleGrid columns={[2, 4]} spacing={4}>
                        <Stat size="sm">
                          <StatLabel color={muted} fontSize="xs">Questions</StatLabel>
                          <StatNumber color={title} fontSize="lg">{stats.total}</StatNumber>
                        </Stat>

                        <Stat size="sm">
                          <StatLabel color={muted} fontSize="xs">Attempted</StatLabel>
                          <StatNumber color="blue.500" fontSize="lg">{stats.attempted}</StatNumber>
                        </Stat>

                        <Stat size="sm">
                          <StatLabel color={muted} fontSize="xs">Score</StatLabel>
                          <StatNumber color={pctColor} fontSize="lg">{percent}%</StatNumber>
                        </Stat>

                        <Stat size="sm">
                          <StatLabel color={muted} fontSize="xs">Marks</StatLabel>
                          <StatNumber color="purple.500" fontSize="lg">
                            {awarded}/{totalPossibleCapped}
                          </StatNumber>
                        </Stat>
                      </SimpleGrid>

                      <Progress
                        value={percent}
                        colorScheme={pctColor}
                        borderRadius="full"
                        size="sm"
                        bg={useColorModeValue("gray.100", "gray.700")}
                      />
                    </VStack>
                  </Box>
                  <AccordionIcon boxSize="20px" />
                </AccordionButton>

                <AccordionPanel bg={bg} borderTopWidth="1px" borderColor={border} p={6}>
                  <VStack spacing={4} align="stretch">

                    <Box
                      borderWidth="1px"
                      borderColor={border}
                      borderRadius="lg"
                      p={4}
                      bg={useColorModeValue("white", "gray.800")}
                      boxShadow="sm"
                    >
                      <Heading size="sm" color={title} mb={3} fontSize="md">
                        Attempt Summary
                      </Heading>

                      <SimpleGrid columns={[1, 2, 4]} spacing={4}>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color={muted} fontWeight="medium">Total Marks</Text>
                          <Text fontSize="md" fontWeight="bold" color={title}>
                            {att.questionPaper?.totalMarks ?? att.totalMarks}
                          </Text>
                        </VStack>

                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color={muted} fontWeight="medium">Questions</Text>
                          <Text fontSize="md" fontWeight="bold" color={title}>
                            {stats.total}
                          </Text>
                        </VStack>

                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color={muted} fontWeight="medium">Time Taken</Text>
                          <Text fontSize="md" fontWeight="bold" color={title}>
                            {Math.round((att.timeTaken || 0) / 60)} min
                          </Text>
                        </VStack>

                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color={muted} fontWeight="medium">Performance</Text>
                          <HStack>
                            <Text fontSize="md" fontWeight="bold" color={pctColor}>
                              {percent}%
                            </Text>
                            <Badge colorScheme={pctColor} variant="subtle" fontSize="xs">
                              {awarded}/{totalPossibleCapped}
                            </Badge>
                          </HStack>
                        </VStack>
                      </SimpleGrid>
                    </Box>

                    {att.adminComment && (
                      <Box
                        bg={surface}
                        borderWidth="1px"
                        borderColor={border}
                        borderRadius="lg"
                        p={4}
                      >
                        <HStack spacing={2} mb={2}>
                          <Icon as={FiMessageCircle} color={muted} boxSize="16px" />
                          <Text fontWeight="bold" color={title} fontSize="sm">
                            Admin Comment
                          </Text>
                        </HStack>
                        <Text color={text} whiteSpace="pre-wrap" lineHeight="1.5" fontSize="sm">
                          {att.adminComment}
                        </Text>
                      </Box>
                    )}

                    <Box borderTopWidth="1px" borderColor={border} pt={4}>
                      <Flex justify="flex-end">
                        <Button
                          colorScheme="blue"
                          size="md"
                          leftIcon={<Icon as={FiFileText} boxSize="16px" />}
                          onClick={() => handleReviewClick(att)}
                          px={6}
                          fontSize="sm"
                        >
                          Review Submission
                        </Button>
                      </Flex>
                    </Box>

                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Box>

      {/* Review Modal */}
      {selectedAttempt && (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader position="sticky" top="0" zIndex={1} bg={bg} borderBottomWidth="1px" borderColor={border} p={4}>
              <VStack align="stretch" spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color={title}>
                  {selectedAttempt.student?.name} — {selectedAttempt.questionPaper?.title}
                </Text>

                <HStack spacing={3}>
                  <Badge colorScheme="blue" variant="solid" px={2} py={1} fontSize="xs">
                    Awarded: {totalAwardedComputed}
                  </Badge>
                  <Badge colorScheme="purple" variant="solid" px={2} py={1} fontSize="xs">
                    Total: {totalPossibleCapped}
                  </Badge>
                  <Badge colorScheme={overallPercent >= 50 ? "green" : "red"} variant="solid" px={2} py={1} fontSize="xs">
                    {overallPercent}%
                  </Badge>
                </HStack>

                <Progress
                  value={overallPercent}
                  colorScheme={overallPercent >= 50 ? "green" : "red"}
                  borderRadius="md"
                  size="md"
                />
              </VStack>
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody p={6}>
              {/* Objective Questions */}
              <Box mb={8}>
                <Heading size="sm" color={title} mb={4} fontSize="md">
                  Objective Questions (MCQ, True/False)
                </Heading>
                <VStack spacing={4} align="stretch">
                  {reviewData.map((item, index) => {
                    const q = item.question;
                    if (!isCheckableType(q?.type)) return null;

                    const finalCorrectness = getFinalCorrectness(item);
                    const qid = String(getIdFromQuestion(q) || item.question);
                    const possible = possibleMap.get(qid) || 0;

                    return (
                      <Box
                        key={`obj-${index}`}
                        p={4}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg={useColorModeValue("white", "gray.800")}
                        borderColor={border}
                        boxShadow="sm"
                        opacity={item.attempted ? 1 : 0.6}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Flex justify="space-between" align="start">
                            <Box flex="1">
                              <Text fontWeight="bold" fontSize="sm" color={title} mb={2}>
                                Q{index + 1}: {q?.question}
                              </Text>
                            </Box>

                            <VStack align="end" spacing={1}>
                              <Badge
                                colorScheme={item.attempted ? "blue" : "gray"}
                                variant="solid"
                                px={2}
                                py={0.5}
                                fontSize="xs"
                              >
                                {item.attempted ? "Attempted" : "Not Attempted"}
                              </Badge>
                              <Badge
                                colorScheme={finalCorrectness ? "green" : "red"}
                                variant="solid"
                                px={2}
                                py={0.5}
                                fontSize="xs"
                              >
                                {finalCorrectness ? "Correct" : "Incorrect"}
                              </Badge>
                            </VStack>
                          </Flex>

                          {/* Show Options with Images */}
                          <OptionsBlock item={item} />

                          <SimpleGrid columns={[1, 2]} spacing={4}>
                            <Box>
                              <Text fontSize="xs" color={muted} mb={1} fontWeight="medium">
                                Student Answer
                              </Text>
                              <Box
                                p={3}
                                bg={useColorModeValue("gray.50", "gray.900")}
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor={border}
                              >
                                <Text color={title} fontWeight="medium" fontSize="sm">
                                  {formatStudentAnswer(item)}
                                </Text>
                              </Box>
                            </Box>

                            <Box>
                              <Text fontSize="xs" color={muted} mb={1} fontWeight="medium">
                                Correct Answer
                              </Text>
                              <Box
                                p={3}
                                bg={useColorModeValue("green.50", "green.900")}
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor={useColorModeValue("green.200", "green.700")}
                              >
                                <Text color={title} fontWeight="medium" fontSize="sm">
                                  {formatCorrectAnswer(item)}
                                </Text>
                              </Box>
                            </Box>
                          </SimpleGrid>

                          <Flex justify="space-between" align="center" pt={2} borderTopWidth="1px" borderColor={border}>
                            <Text fontSize="xs" color={muted}>
                              <strong>Possible Marks:</strong> {possible}
                            </Text>

                            <Badge
                              variant="solid"
                              colorScheme={finalCorrectness && item.attempted ? "green" : "red"}
                              px={3}
                              py={1}
                              fontSize="xs"
                            >
                              Awarded: {finalCorrectness && item.attempted ? possible : 0}
                            </Badge>
                          </Flex>

                          <QuestionImage question={q} />
                          <JustificationImage item={item} />
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>

              {/* Subjective Questions */}
              <Box mb={6}>
                <Heading size="sm" color={title} mb={4} fontSize="md">
                  Subjective Questions (Short, Long, Diagram)
                </Heading>
                <VStack spacing={6} align="stretch">
                  {reviewData.map((item, index) => {
                    const q = item.question;
                    const type = q?.type?.toLowerCase();
                    if (isCheckableType(type)) return null;

                    const qid = String(getIdFromQuestion(q) || item.question);
                    const possible = possibleMap.get(qid) || 0;
                    const disabled = !item.attempted;
                    const finalCorrectness = getFinalCorrectness(item);
                    const uploads: string[] = Array.isArray(item.uploads) ? item.uploads : [];

                    return (
                      <Box
                        key={`sub-${index}`}
                        p={4}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg={useColorModeValue("white", "gray.800")}
                        borderColor={border}
                        boxShadow="sm"
                        opacity={disabled ? 0.6 : 1}
                      >
                        <VStack align="stretch" spacing={4}>
                          <Flex justify="space-between" align="start">
                            <Box flex="1">
                              <Text fontWeight="bold" fontSize="sm" color={title} mb={1}>
                                Q{index + 1}: {q?.question}
                              </Text>
                              <Badge variant="outline" colorScheme="blue" fontSize="xs">
                                {type?.toUpperCase()} Question
                              </Badge>
                            </Box>

                            <Badge
                              colorScheme={item.attempted ? "blue" : "gray"}
                              variant="solid"
                              px={2}
                              py={0.5}
                              fontSize="xs"
                            >
                              {item.attempted ? "Attempted" : "Not Attempted"}
                            </Badge>
                          </Flex>

                          {type === "diagram" && <DiagramImage question={q} />}

                          <Box>
                            <Text fontSize="xs" color={muted} mb={2} fontWeight="medium">
                              Student Answer
                            </Text>
                            <Box
                              p={3}
                              bg={useColorModeValue("gray.50", "gray.900")}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={border}
                              minH="80px"
                            >
                              <Text
                                whiteSpace="pre-wrap"
                                color={title}
                                lineHeight="1.5"
                                fontSize="sm"
                              >
                                {formatStudentAnswer(item)}
                              </Text>
                            </Box>
                          </Box>

                          <SubmittedUploads uploads={uploads} />
                          <JustificationImage item={item} />

                          <Box borderTopWidth="1px" borderColor={border} pt={4}>
                            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                              <Box>
                                <Text fontSize="xs" color={muted} mb={1} fontWeight="medium">
                                  Review Status
                                </Text>
                                <Select
                                  value={item.isCorrect === true ? "true" : item.isCorrect === false ? "false" : "pending"}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    let newIsCorrect;
                                    if (value === "true") {
                                      newIsCorrect = true;
                                    } else if (value === "false") {
                                      newIsCorrect = false;
                                    } else {
                                      newIsCorrect = undefined;
                                    }
                                    updateReviewItem(index, {
                                      isCorrect: newIsCorrect,
                                    });
                                  }}
                                  size="sm"
                                  w="200px"
                                  bg={bg}
                                  borderColor={border}
                                  isDisabled={disabled}
                                  fontSize="sm"
                                >
                                  <option value="pending">Pending Review</option>
                                  <option value="false">Mark as Incorrect</option>
                                  <option value="true">Mark as Correct</option>
                                </Select>
                              </Box>

                              <VStack align="end" spacing={1}>
                                <HStack spacing={3}>
                                  <Badge variant="outline" colorScheme="blue" px={2} py={0.5} fontSize="xs">
                                    Possible: {possible}
                                  </Badge>
                                  <Badge
                                    variant="solid"
                                    colorScheme={item.isCorrect === true && item.attempted ? "green" : "red"}
                                    px={2}
                                    py={0.5}
                                    fontSize="xs"
                                  >
                                    Awarded: {item.isCorrect === true && item.attempted ? possible : 0}
                                  </Badge>
                                </HStack>

                                {item.isCorrect === undefined && item.attempted && (
                                  <Badge colorScheme="yellow" variant="solid" px={2} py={0.5} fontSize="xs">
                                    Pending Review
                                  </Badge>
                                )}

                                {!item.attempted && (
                                  <Badge colorScheme="gray" variant="solid" px={2} py={0.5} fontSize="xs">
                                    Not Attempted
                                  </Badge>
                                )}
                              </VStack>
                            </Flex>
                          </Box>
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>

              {/* Overall Feedback */}
              <Box borderTopWidth="2px" borderColor={border} pt={6}>
                <Heading size="sm" color={title} mb={3} fontSize="md">
                  Overall Feedback
                </Heading>
                <Textarea
                  placeholder="Add overall feedback for this submission..."
                  value={overallComment}
                  onChange={(e) => setOverallComment(e.target.value)}
                  rows={4}
                  bg={bg}
                  borderColor={border}
                  fontSize="sm"
                  resize="vertical"
                />
              </Box>
            </ModalBody>

            <ModalFooter position="sticky" bottom="0" bg={bg} borderTopWidth="1px" borderColor={border} p={4}>
              <Flex justify="space-between" align="center" w="100%" wrap="wrap" gap={4}>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color={muted}>
                    Final Score Calculation
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color={title}>
                    {totalAwardedComputed} / {totalPossibleCapped} ({overallPercent}%)
                  </Text>
                </VStack>

                <HStack spacing={3}>
                  <Button variant="ghost" onClick={onClose} size="md" fontSize="sm">
                    Cancel
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleSaveReview}
                    size="md"
                    px={6}
                    fontSize="sm"
                  >
                    Save Review
                  </Button>
                </HStack>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}